import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Shield, Users, ClipboardList, IndianRupee, Plus, Clock, FileCheck, FileText, Download, RefreshCw, CreditCard, UserMinus, Building2, QrCode } from 'lucide-react';
import StatCard from '../components/StatCard';
import LoadingSpinner from '../components/LoadingSpinner';
import { selfAPI } from '../services/api';
import { formatCurrency } from '../utils/formatters';

const REJECTION_LABELS = {
  invalid_documents: 'Invalid Documents',
  incorrect_information: 'Incorrect Information',
  relationship_proof_missing: 'Relationship Proof Missing',
  other: 'Other',
};

export default function EmployeeDashboard() {
  const [data, setData] = useState(null);
  const [policy, setPolicy] = useState(null);
  const [requests, setRequests] = useState([]);
  const [policyDocs, setPolicyDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const location = useLocation();

  useEffect(() => {
    Promise.all([
      selfAPI.summary(),
      selfAPI.policy().catch(() => ({ data: null })),
      selfAPI.familyRequests().catch(() => ({ data: [] })),
      selfAPI.policyDocuments().catch(() => ({ data: [] })),
    ])
      .then(([summaryRes, policyRes, reqRes, docsRes]) => {
        setData(summaryRes.data);
        setPolicy(policyRes.data);
        setRequests(reqRes.data || []);
        setPolicyDocs(docsRes.data || []);
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <LoadingSpinner />;
  if (!data) return <p className="text-slate-500">Unable to load your coverage.</p>;

  const { employee, familyMembers } = data;
  const dependents = (familyMembers || []).filter((m) => m.relation !== 'self');
  const windowOpen = policy && !policy.enrollmentWindow?.isLocked && !policy.pendingRequest;
  const resubmitable = policy?.resubmitableRequest;
  const canResubmit = resubmitable && !policy?.pendingRequest;
  const latestRejected = requests.find((r) => r.status === 'rejected');

  const downloadPolicyDoc = async (doc) => {
    const { data } = await selfAPI.downloadPolicyDocument(doc.id);
    const url = URL.createObjectURL(data);
    const a = document.createElement('a');
    a.href = url;
    a.download = doc.file_name;
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadEcard = async () => {
    const { data } = await selfAPI.downloadEcard();
    const url = URL.createObjectURL(data);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ecard-${employee.employee_code || 'coverage'}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadEcardQr = async () => {
    const { data } = await selfAPI.downloadEcardQr();
    const url = URL.createObjectURL(data);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ecard-qr-${employee.employee_code || 'coverage'}.png`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const benefits = policy?.benefits;
  const premiumBreakdown = policy?.premiumBreakdown || [];
  const canRemove = !policy?.pendingRequest;

  const DOC_LABELS = {
    policy_schedule: 'Policy Schedule',
    benefit_grid: 'Benefit Grid',
    other: 'Policy Document',
  };

  return (
    <div className="space-y-6">
      {location.state?.message && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-sm text-green-800">
          {location.state.message}
        </div>
      )}

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">My Coverage</h1>
          <p className="text-slate-500 mt-1">View policy, family members, and approval status</p>
        </div>
        {windowOpen && (
          <Link to="/my-family" className="btn-primary flex items-center gap-2 self-start">
            <Plus className="w-4 h-4" />
            Add Family Member
          </Link>
        )}
        {canResubmit && (
          <Link to={`/my-family?resubmit=${resubmitable.id}`} className="btn-primary flex items-center gap-2 self-start">
            <RefreshCw className="w-4 h-4" />
            Resubmit Rejected Request
          </Link>
        )}
        <button type="button" onClick={downloadEcard} className="btn-secondary flex items-center gap-2 self-start">
          <CreditCard className="w-4 h-4" />
          Download E-Card
        </button>
        <button type="button" onClick={downloadEcardQr} className="btn-secondary flex items-center gap-2 self-start">
          <QrCode className="w-4 h-4" />
          Save QR to Phone
        </button>
        <Link to="/hospitals" className="btn-secondary flex items-center gap-2 self-start">
          <Building2 className="w-4 h-4" />
          Find Hospitals
        </Link>
        <Link to="/my-profile" className="btn-secondary flex items-center gap-2 self-start">
          My Profile
        </Link>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <StatCard title="Policy" value={employee.policy_number} icon={Shield} color="primary" />
        <StatCard title="Sum Insured" value={formatCurrency(employee.sum_insured)} icon={Shield} color="purple" />
        <StatCard title="My Premium" value={formatCurrency(employee.total_premium)} icon={IndianRupee} color="amber" />
        <StatCard title="Pending Claims" value={data.claimsPending || 0} icon={ClipboardList} color="red" />
      </div>

      <div className="card">
        <h2 className="text-lg font-semibold mb-4">Policy Details</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
          <div><span className="text-slate-400">Policy Name</span><p className="font-medium">{policy?.policyName || employee.company_name}</p></div>
          <div><span className="text-slate-400">Insurer</span><p>{policy?.providerName || employee.provider_name}</p></div>
          <div><span className="text-slate-400">Coverage</span><p>{formatCurrency(policy?.coverageAmount || employee.sum_insured)}</p></div>
          <div><span className="text-slate-400">Status</span><p className="capitalize">{policy?.policyStatus || 'active'}</p></div>
          <div><span className="text-slate-400">Valid From</span><p>{policy?.policyStartDate ? new Date(policy.policyStartDate).toLocaleDateString('en-IN') : '—'}</p></div>
          <div><span className="text-slate-400">Valid Until</span><p>{new Date(employee.policy_end_date).toLocaleDateString('en-IN')}</p></div>
          <div><span className="text-slate-400">Members Covered</span><p>{policy?.membersCovered ?? familyMembers?.length ?? 0}</p></div>
          <div><span className="text-slate-400">Current Premium</span><p className="font-semibold">{formatCurrency(policy?.currentPremium || employee.total_premium)}</p></div>
        </div>
        {policy?.enrollmentWindow && (
          <div className={`mt-4 p-3 rounded-lg text-sm flex items-center gap-2 ${policy.enrollmentWindow.isLocked ? 'bg-amber-50 text-amber-800' : 'bg-green-50 text-green-800'}`}>
            <Clock className="w-4 h-4" />
            {policy.enrollmentWindow.isLocked
              ? policy.enrollmentWindow.message
              : policy.enrollmentWindow.overrideUntil
                ? `Family enrollment open (admin extended until ${new Date(policy.enrollmentWindow.overrideUntil).toLocaleDateString('en-IN')}) — ${policy.enrollmentWindow.daysRemaining} day(s) left`
                : `Family enrollment open — ${policy.enrollmentWindow.daysRemaining} day(s) remaining (until ${new Date(policy.enrollmentWindow.windowEndDate).toLocaleDateString('en-IN')})`}
          </div>
        )}
      </div>

      {benefits && (
        <div className="card">
          <h2 className="text-lg font-semibold mb-4">Benefit Summary</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
            <div><span className="text-slate-400">Room Rent Limit</span><p>{benefits.roomRentLimit ? formatCurrency(benefits.roomRentLimit) : 'As per policy'}</p></div>
            <div><span className="text-slate-400">Co-payment</span><p>{benefits.coPaymentPercentage ? `${benefits.coPaymentPercentage}%` : 'None'}</p></div>
            <div><span className="text-slate-400">Waiting Period</span><p>{benefits.waitingPeriodDays ? `${benefits.waitingPeriodDays} days` : 'None'}</p></div>
            <div><span className="text-slate-400">Maternity</span><p>{benefits.maternityCoverage ? (benefits.maternitySubLimit ? formatCurrency(benefits.maternitySubLimit) : 'Covered') : 'Not covered'}</p></div>
            <div><span className="text-slate-400">Corporate Buffer</span><p>{benefits.corporateBuffer ? formatCurrency(benefits.corporateBuffer) : '—'}</p></div>
            <div><span className="text-slate-400">Claim TAT</span><p>{benefits.claimSettlementTat ? `${benefits.claimSettlementTat} days` : '—'}</p></div>
            <div><span className="text-slate-400">Max Child Age</span><p>{benefits.maxChildAge ?? '—'}</p></div>
            <div><span className="text-slate-400">In-laws Eligible</span><p>{benefits.inLawsEligible ? 'Yes' : 'No'}</p></div>
          </div>
        </div>
      )}

      {premiumBreakdown.length > 0 && (
        <div className="card">
          <h2 className="text-lg font-semibold mb-4">Premium Breakdown</h2>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-slate-500 border-b text-left">
                <th className="pb-2">Member</th><th className="pb-2">Relation</th><th className="pb-2">Pro-rata</th><th className="pb-2">GST</th><th className="pb-2">Total</th>
              </tr>
            </thead>
            <tbody>
              {premiumBreakdown.map((row) => (
                <tr key={row.id} className="border-b border-slate-100">
                  <td className="py-2 font-medium">{row.member_name}</td>
                  <td className="capitalize">{row.relation?.replace(/_/g, ' ')}</td>
                  <td>{formatCurrency(row.pro_rata_premium)}</td>
                  <td>{formatCurrency(row.gst_amount)}</td>
                  <td className="font-medium">{formatCurrency(row.total)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={4} className="pt-3 text-right font-semibold text-slate-600">Household Total</td>
                <td className="pt-3 font-bold">{formatCurrency(policy?.currentPremium || employee.total_premium)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      <div className="card">
        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <FileText className="w-5 h-5" />
          Policy Documents
        </h2>
        {policyDocs.length === 0 ? (
          <p className="text-sm text-slate-500">No policy documents available yet. Contact HR if you need your policy schedule.</p>
        ) : (
          <div className="space-y-2">
            {policyDocs.map((doc) => (
              <button
                key={doc.id}
                type="button"
                onClick={() => downloadPolicyDoc(doc)}
                className="w-full flex items-center justify-between p-3 rounded-lg border border-slate-100 hover:bg-slate-50 text-left text-sm"
              >
                <span>
                  <span className="font-medium">{DOC_LABELS[doc.document_type] || doc.document_type}</span>
                  <span className="text-slate-400 block text-xs">{doc.file_name}</span>
                </span>
                <Download className="w-4 h-4 text-primary-600 shrink-0" />
              </button>
            ))}
          </div>
        )}
      </div>

      {requests.length > 0 && (
        <div className="card">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2"><FileCheck className="w-5 h-5" />Approval Requests</h2>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-slate-500 border-b text-left">
                <th className="pb-2">Request</th><th className="pb-2">Type</th><th className="pb-2">Submitted</th><th className="pb-2">Premium Change</th><th className="pb-2">Status</th><th className="pb-2"></th>
              </tr>
            </thead>
            <tbody>
              {requests.map((r) => (
                <tr key={r.id} className="border-b border-slate-100">
                  <td className="py-2 font-medium">
                    <Link to={`/my-family/requests/${r.id}`} className="text-primary-600 hover:underline">{r.request_number}</Link>
                  </td>
                  <td className="capitalize">{r.request_type}</td>
                  <td>{new Date(r.submitted_at).toLocaleDateString('en-IN')}</td>
                  <td>{r.premium_difference > 0 ? '+' : ''}{formatCurrency(r.premium_difference)}</td>
                  <td>
                    <span className={`capitalize px-2 py-0.5 rounded-full text-xs ${
                      r.status === 'rejected' ? 'bg-red-50 text-red-700'
                        : r.status === 'approved' ? 'bg-green-50 text-green-700'
                          : r.status === 'cancelled' ? 'bg-slate-100 text-slate-600'
                            : r.status === 'superseded' ? 'bg-slate-100 text-slate-500'
                              : 'bg-amber-50 text-amber-700'
                    }`}
                    >
                      {r.status}
                    </span>
                  </td>
                  <td className="py-2 space-x-2">
                    <Link to={`/my-family/requests/${r.id}`} className="text-primary-600 text-xs hover:underline">View</Link>
                    {r.status === 'rejected' && !policy?.pendingRequest && (
                      <Link to={`/my-family?resubmit=${r.id}`} className="text-primary-600 text-xs hover:underline">
                        Resubmit
                      </Link>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {latestRejected && (
            <div className="mt-3 p-3 bg-red-50 rounded-lg text-sm text-red-900">
              <p className="font-medium">{latestRejected.request_number} — {REJECTION_LABELS[latestRejected.rejection_reason_code] || latestRejected.rejection_reason_code}</p>
              <p className="mt-1">{latestRejected.rejection_remarks}</p>
            </div>
          )}
        </div>
      )}

      <div className="card">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold flex items-center gap-2"><Users className="w-5 h-5" />Family Members</h2>
          <Link to="/claims" className="text-sm text-primary-600">Raise a claim →</Link>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-slate-500 border-b text-left">
              <th className="pb-2">Name</th><th className="pb-2">Relation</th><th className="pb-2">Age</th><th className="pb-2">Premium</th><th className="pb-2"></th>
            </tr>
          </thead>
          <tbody>
            {(familyMembers || []).map((m) => (
              <tr key={m.id} className="border-b border-slate-100">
                <td className="py-2 font-medium">{m.member_name}</td>
                <td className="capitalize">{m.relation?.replace(/_/g, ' ')}</td>
                <td>{m.age}</td>
                <td>{formatCurrency(parseFloat(m.pro_rata_premium) + parseFloat(m.gst_amount))}</td>
                <td className="py-2">
                  {m.relation !== 'self' && canRemove && (
                    <Link to={`/my-family/remove/${m.id}`} className="text-red-600 text-xs hover:underline inline-flex items-center gap-1">
                      <UserMinus className="w-3 h-3" /> Remove
                    </Link>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {dependents.length === 0 && <p className="text-slate-500 text-sm mt-2">No dependents on record.</p>}
      </div>
    </div>
  );
}
